/* eslint-disable max-classes-per-file */
// eslint-disable-next-line no-restricted-syntax
import * as engagementPlugin from '@amplitude/engagement-browser';

import type { Locale } from '@change-corgi/config/locales';
import type { EventTrackingListener, TrackingEvent, TrackingEventProperties } from '@change-corgi/core/eventTracker';
import { getWindow } from '@change-corgi/core/window';

import type { AmplitudeLocale } from './locale';
import { AMPLITUDE_LOCALE_MAP } from './locale';

abstract class AmplitudeGuidesAndSurveys implements EventTrackingListener {
	private enabled = false;
	protected loggingEnabled = false;
	private running = false;
	protected eventTracker?: {
		track: (eventType: string, eventProperties?: Record<string, unknown>) => Promise<boolean>;
	};

	setEventTracker(eventTracker: {
		track: (eventType: string, eventProperties?: Record<string, unknown>) => Promise<boolean>;
	}): this {
		this.eventTracker = eventTracker;
		return this;
	}

	setEnabled(enabled: boolean): this {
		this.enabled = enabled;
		this.onChange();
		return this;
	}

	setLoggingEnabled(loggingEnabled: boolean): this {
		this.loggingEnabled = loggingEnabled;
		return this;
	}

	protected log(message: string, ...args: unknown[]): void {
		if (this.loggingEnabled) {
			// eslint-disable-next-line no-console
			console.log(message, ...args);
		}
	}

	protected abstract start(): void;

	protected isEnabled(): boolean {
		if (!this.enabled) {
			return false;
		}
		return true;
	}

	private onChange(): void {
		if (this.isEnabled()) {
			if (!this.running) {
				this.start();
				this.running = true;
			}
			return;
		}
		if (this.running) {
			this.running = false;
		}
	}

	onEventsTrack(events: readonly TrackingEvent[], commonData: Readonly<TrackingEventProperties>): void {
		if (!this.isEnabled() || !this.running) {
			return;
		}

		events.forEach((event) => {
			this.forwardEvent(event, commonData);
		});
	}

	protected abstract forwardEvent(event: TrackingEvent, commonData: Readonly<TrackingEventProperties>): void;
}

class FakeAmplitudeGuidesAndSurveys extends AmplitudeGuidesAndSurveys {
	protected start(): void {
		this.log('[FAKE] Amplitude engagement starting');
	}

	protected forwardEvent(event: TrackingEvent, commonData: Readonly<TrackingEventProperties>): void {
		this.log('[FAKE] Forwarding event:', event.name, {
			event_type: event.name,
			event_properties: { ...commonData, ...event.data },
		});
	}
}

type RealAmplitudeGuidesAndSurveysOptions = {
	apiKey: string;
	deviceId?: string;
	locale?: Locale;
};

function mapLocaleToAmplitudeLocale(locale: Locale | undefined): AmplitudeLocale | undefined {
	if (locale) {
		return AMPLITUDE_LOCALE_MAP[locale];
	}
	return undefined;
}

class RealAmplitudeGuidesAndSurveys extends AmplitudeGuidesAndSurveys {
	private options: RealAmplitudeGuidesAndSurveysOptions;

	constructor(options: RealAmplitudeGuidesAndSurveysOptions) {
		super();
		this.options = options;
	}

	protected start(): void {
		this.log('Amplitude engagement starting');
		const amplitudeLocale = mapLocaleToAmplitudeLocale(this.options.locale);
		engagementPlugin.init(this.options.apiKey, { locale: amplitudeLocale });

		const window = getWindow();
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (window.engagement) {
			void window.engagement.boot({
				user: {
					device_id: this.options.deviceId, // Is the UUID
					user_properties: {},
				},
				integrations: [
					{
						track: (event) => {
							if (this.eventTracker) {
								// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
								void this.eventTracker.track(event.event_type, event.event_properties as any);
							}
						},
					},
				],
			});
		}
	}

	protected forwardEvent(event: TrackingEvent, commonData: Readonly<TrackingEventProperties>): void {
		this.log('Forwarding event to Amplitude:', event.name);
		const window = getWindow();
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		window.engagement?.forwardEvent({
			event_type: event.name,
			event_properties: { ...commonData, ...event.data },
		});
	}
}

export type { AmplitudeGuidesAndSurveys };

export function createFakeAmplitudeGuidesAndSurveys(): AmplitudeGuidesAndSurveys {
	return new FakeAmplitudeGuidesAndSurveys();
}

export function createAmplitudeGuidesAndSurveys(
	options: RealAmplitudeGuidesAndSurveysOptions,
): AmplitudeGuidesAndSurveys {
	return new RealAmplitudeGuidesAndSurveys(options);
}
